import { IllustratedRouteLayout, type ResponsiveDevice } from "@src/common/IllustratedRouteLayout";
import { InvocaPhoneNumber } from "@src/common/InvocaPhoneNumber";
import { BodyText, HeadingDiv } from "@src/common/styled-components";
import { MEDIA_QUERIES } from "@src/constants/media-queries";
import { useApplicationContext } from "@src/context/ApplicationContext";
import { useEnvironment } from "@src/context/EnvironmentContext";
import { useTimerContext } from "@src/context/TimerContext";
import ClientLogsService from "@src/services/client-logs.service";
import { useMediaQuery } from "@uhg-abyss/web/hooks/useMediaQuery";
import { styled } from "@uhg-abyss/web/tools/styled";
import { Button } from "@uhg-abyss/web/ui/Button";
import { useEffect } from "react";

// Styled Components
const ButtonContainer = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  minWidth: "300px",
});

const ContactInfo = styled("div", {
  fontSize: 14,
  color: "$app.color.text.primary",
});

// Constants
const BUSINESS_HOURS = "Mon-Fri 9 a.m. - 5 p.m.";

export const ContactUs = () => {
  const isMobile = useMediaQuery(MEDIA_QUERIES.mobileMax);
  const isTablet = useMediaQuery(MEDIA_QUERIES.tabletMax);
  const { CONTACT_US_WEBFORM_URL } = useEnvironment();
  const { isTimerRunning, stopTimer } = useTimerContext();
  const { data, setData, clearDataExcluding, clearCompletedRoutes, clearAdditionalSavedModalData } =
    useApplicationContext();

  useEffect(() => {
    setData("isReturningFromContactFlow", true); // Set flag to indicate user has been on Contact Us page
    clearDataExcluding(["userUuid", "isReturningFromContactFlow"]); // Clear all context data to ensure a fresh state for the Contact Us page, especially for users who navigate here from different entry points in the app
    clearCompletedRoutes(); // Clear completed routes to reset navigation flow for users who navigate to Contact Us page from different entry points in the app
    clearAdditionalSavedModalData(); // Clear any additional saved modal data to ensure a fresh state for the Contact Us page
  }, []);

  useEffect(() => {
    // Stop the timer when this component mounts
    if (isTimerRunning) {
      stopTimer();
    }
  }, [isTimerRunning, stopTimer]);

  // Helper functions
  const handleContactUsClick = () => {
    if (CONTACT_US_WEBFORM_URL) {
      window.open(CONTACT_US_WEBFORM_URL, "_blank");
    } else {
      ClientLogsService.logError(
        data.userUuid || "unknown",
        "ContactUs.handleContactUsClick",
        "Contact form URL is not available.",
      );
    }
  };

  let device: ResponsiveDevice = "desktop";
  if (isMobile) {
    device = "mobile";
  } else if (isTablet) {
    device = "tablet";
  }

  return (
    <IllustratedRouteLayout metaTitle="Contact Us" device={device}>
      <HeadingDiv device={device}>We weren't able to complete scheduling</HeadingDiv>
      <BodyText device={device} mobileBrandColor>
        Some situations require a quick call to complete scheduling. Please call or fill out our
        contact form to finish setting up your appointment.
      </BodyText>
      <ButtonContainer>
        <Button
          data-testid="test-id-contact-us-button"
          variant="filled"
          size={isMobile ? "$lg" : "$md"}
          id="obc-contact-us-contact-pg-btn"
          data-obc-label="Contact Us Button on Contact Us Page"
          onClick={handleContactUsClick}
        >
          Contact us
        </Button>

        <ContactInfo>
          or call{" "}
          <span style={{ fontWeight: "700" }}>
            <InvocaPhoneNumber />
          </span>
          , {BUSINESS_HOURS}
        </ContactInfo>
      </ButtonContainer>
    </IllustratedRouteLayout>
  );
};
